import { FormHTMLAttributes, ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface ModalProps {
    open: boolean
    onClose?: () => void
    children: ReactNode
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    // Default true. Set false to stop a stray backdrop click / Escape from discarding in-progress
    // work (e.g. a reviewed-but-unsaved form). The explicit Cancel/X button still closes.
    closeOnBackdropClick?: boolean
    closeOnEscape?: boolean
    'data-testid'?: string
}

interface ModalHeaderProps {
    children: ReactNode
    onClose?: () => void
}

interface ModalBodyProps {
    children: ReactNode
    className?: string
}

interface ModalFooterProps {
    children: ReactNode
    className?: string
}

const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    '2xl': 'max-w-7xl',
}

export const Modal = ({ open, onClose, children, size = 'md', closeOnBackdropClick = true, closeOnEscape = true, 'data-testid': dataTestId }: ModalProps) => {
    const mouseDownOnBackdrop = useRef(false)

    useEffect(() => {
        if (!open || !closeOnEscape) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose?.()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [open, onClose, closeOnEscape])

    if (!open) return null

    // Portal to body so the fixed-position overlay escapes any transformed/overflow ancestor
    // (e.g. dnd-kit drag contexts or scroll containers), which would otherwise trap it and
    // squeeze the panel — causing horizontal overflow inside the modal body.
    return createPortal(
        <div
            // p-4: keeps the panel off the viewport edges on narrow screens (the panel is w-full
            // up to its size cap, so without it the modal touches both edges on a phone).
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm dark:bg-black/60"
            onMouseDown={(e) => {
                mouseDownOnBackdrop.current = e.target === e.currentTarget
            }}
            onClick={(e) => {
                if (closeOnBackdropClick && mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose?.()
            }}
        >
            <div
                data-testid={dataTestId}
                className={`relative flex max-h-full w-full ${sizeClasses[size]} flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:shadow-slate-900/50`}
            >
                {children}
            </div>
        </div>,
        document.body
    )
}

export const ModalHeader = ({ children, onClose }: ModalHeaderProps) => (
    <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{children}</h2>
        {onClose && (
            <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground-muted transition-colors duration-150 hover:bg-default hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label="Close dialog"
            >
                <X className="h-4 w-4" />
            </button>
        )}
    </div>
)

export const ModalBody = ({ children, className = '' }: ModalBodyProps) => (
    <div className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 ${className}`}>{children}</div>
)

/**
 * Wrapper for modals whose body and footer live inside a `<form>`.
 *
 * The panel is a height-capped flex column and `ModalBody` scrolls via `flex-1 overflow-y-auto` —
 * but only while the body and footer are flex children OF THE PANEL. A plain `<form>` in between
 * is a block box sized to its content, so the body never receives a bounded height, nothing
 * scrolls, and the panel's `overflow-hidden` clips the footer: on a short viewport the
 * Cancel/Save buttons become unreachable. This keeps the form inside the flex chain and lets it
 * shrink, so the body scrolls and the footer stays pinned and visible.
 */
export const ModalForm = ({ children, className = '', ...formProps }: FormHTMLAttributes<HTMLFormElement>) => (
    <form {...formProps} className={`flex min-h-0 flex-1 flex-col ${className}`}>
        {children}
    </form>
)

export const ModalFooter = ({ children, className = '' }: ModalFooterProps) => (
    <div className={`flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 ${className}`}>{children}</div>
)
